import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Volume2, Loader2, ArrowUpRight } from 'lucide-react';
import { useReadAloud } from '../lecture/hooks/useReadAloud';
import { getTermBySlug, getTermByText } from './glossaryMatch';
import type { GlossaryTerm as GlossaryTermRecord } from '../../data/glossary';

interface GlossaryTermProps {
  /** Resolve the entry by slug (preferred). */
  slug?: string;
  /** …or by surface text (used by the markdown auto-linker). */
  text?: string;
  /**
   * The label to render. Defaults to `text`, then the canonical term. Lets the
   * auto-linker preserve the original casing/wording from the slide.
   */
  children?: React.ReactNode;
  className?: string;
}

// Inline, interactive technical term. Renders with a subtle dotted underline and
// a small speaker icon. Click/tap: (a) pronounces the term via the app's shared
// lecture TTS, and (b) opens a small popover with the pronunciation, definition,
// and a link to the full glossary entry. Resilient — if TTS fails the popover
// still opens. If the term can't be resolved it renders plain text, so it never
// breaks slide content.
export default function GlossaryTerm({
  slug,
  text,
  children,
  className = '',
}: GlossaryTermProps) {
  const entry: GlossaryTermRecord | undefined = slug
    ? getTermBySlug(slug)
    : text
      ? getTermByText(text)
      : undefined;

  const { read, state: ttsState } = useReadAloud();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const popId = useId();

  // Close on outside-click / Escape while the popover is open.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = children ?? text ?? entry?.term ?? '';

  // No matching entry — degrade to plain text so markdown stays intact.
  if (!entry) return <>{label}</>;

  const handleActivate = () => {
    setOpen((v) => !v);
    // Speak the canonical term. read() is fire-and-forget and swallows errors,
    // so a failed TTS request never blocks the popover.
    void read(entry.term);
  };

  return (
    <span ref={wrapRef} className={`relative inline ${className}`}>
      <button
        type="button"
        onClick={handleActivate}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${entry.term} — pronounce and define`}
        className="group inline cursor-pointer rounded-sm border-b border-dotted border-ws-accent/60 bg-transparent p-0 text-left font-[inherit] text-inherit underline-offset-2 transition-colors hover:border-ws-accent hover:text-ws-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
      >
        {label}
        <span
          aria-hidden="true"
          className="ml-0.5 inline-flex translate-y-[1px] text-ws-accent/70 transition-colors group-hover:text-ws-accent"
        >
          {ttsState === 'loading' ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <Volume2 size={11} />
          )}
        </span>
      </button>

      {open && (
        <span
          id={popId}
          role="dialog"
          aria-label={`${entry.term} glossary entry`}
          className="ws-pop-in absolute left-0 top-[calc(100%+6px)] z-50 block w-[min(20rem,80vw)] cursor-default rounded-lg border border-ws-border bg-ws-surface p-3 text-left shadow-lg"
          // Keep clicks inside the popover from bubbling to the outside handler.
          onClick={(e) => e.stopPropagation()}
        >
          <span className="mb-1 flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-ws-ink">{entry.term}</span>
            <button
              type="button"
              onClick={() => void read(entry.term)}
              aria-label={`Pronounce ${entry.term}`}
              className="grid size-6 shrink-0 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
            >
              {ttsState === 'loading' ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Volume2 size={13} />
              )}
            </button>
          </span>

          <span className="mb-1.5 block text-[12px] text-ws-ink-3">
            <span className="font-medium text-ws-ink-2">{entry.say}</span>
            {entry.ipa && (
              <span className="ml-1.5 font-mono text-ws-ink-4">{entry.ipa}</span>
            )}
          </span>

          <span className="block text-[12.5px] leading-relaxed text-ws-ink-2">
            {entry.definition}
          </span>

          <Link
            to={`/glossary#${entry.slug}`}
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center gap-0.5 text-[11px] font-medium text-ws-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
          >
            Open in glossary
            <ArrowUpRight size={12} />
          </Link>
        </span>
      )}
    </span>
  );
}
